/**
 * Email Service for Paddle Türkiye
 * Handles all transactional emails via Resend through Supabase Edge Functions
 */

const EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-email`;
const ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

/**
 * Validates email service configuration
 * @returns {Object} Validation result with isValid flag and error message
 */
const validateEmailConfig = () => {
  if (!EDGE_FUNCTION_URL || EDGE_FUNCTION_URL?.includes('undefined')) {
    return {
      isValid: false,
      error: 'Email service not configured - missing Supabase URL'
    };
  }
  if (!ANON_KEY || ANON_KEY === 'undefined') {
    return {
      isValid: false,
      error: 'Email service not configured - missing Supabase anon key'
    };
  }
  return { isValid: true };
};

/**
 * Send welcome email after successful signup
 * @param {string} email - User's email address
 * @param {Object} userData - Additional user data (name, etc.)
 * @returns {Promise<Object>} Response with success status
 */
export const sendWelcomeEmail = async (email, userData = {}) => {
  const configCheck = validateEmailConfig();
  if (!configCheck?.isValid) {
    console.warn('Welcome email skipped:', configCheck?.error);
    return { success: false, error: configCheck?.error, skipped: true };
  }

  try {
    const userName = userData?.name || userData?.full_name || 'Değerli Üyemiz';
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`
      },
      body: JSON.stringify({
        to: email,
        subject: 'Paddle Türkiye\'ye Hoş Geldiniz! 🏓',
        type: 'signup',
        data: {
          name: userName,
          appUrl: window.location?.origin
        }
      })
    });

    if (!response?.ok) {
      const result = await response?.json();
      throw new Error(result?.error || 'Failed to send welcome email');
    }

    const result = await response?.json();
    return { success: true, data: result };
  } catch (error) {
    // More detailed error logging for debugging
    console.error('Welcome email error details:', {
      message: error?.message,
      endpoint: EDGE_FUNCTION_URL,
      hasAuth: !!ANON_KEY
    });
    return { success: false, error: error?.message };
  }
};

/**
 * Send email verification link
 * @param {string} email - User's email address
 * @param {string} verificationUrl - Verification URL from Supabase
 * @returns {Promise<Object>} Response with success status
 */
export const sendVerificationEmail = async (email, verificationUrl) => {
  const configCheck = validateEmailConfig();
  if (!configCheck?.isValid) {
    console.warn('Verification email skipped:', configCheck?.error);
    return { success: false, error: configCheck?.error, skipped: true };
  }

  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`
      },
      body: JSON.stringify({
        type: 'verification',
        email,
        data: {
          verificationUrl
        }
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result.error || 'Failed to send verification email');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Verification email error:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send password reset email
 * @param {string} email - User's email address
 * @param {string} resetUrl - Password reset URL from Supabase
 * @returns {Promise<Object>} Response with success status
 */
export const sendPasswordResetEmail = async (email, resetUrl) => {
  const configCheck = validateEmailConfig();
  if (!configCheck?.isValid) {
    console.warn('Password reset email skipped:', configCheck?.error);
    return { success: false, error: configCheck?.error, skipped: true };
  }

  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`
      },
      body: JSON.stringify({
        type: 'password_reset',
        email,
        data: {
          resetUrl
        }
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result.error || 'Failed to send password reset email');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Password reset email error:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send order confirmation email after successful purchase
 * @param {string} email - Customer's email address
 * @param {Object} orderData - Order details (orderId, total, items, shipping)
 * @returns {Promise<Object>} Response with success status
 */
export const sendOrderConfirmationEmail = async (email, orderData) => {
  const configCheck = validateEmailConfig();
  if (!configCheck?.isValid) {
    console.warn('Order confirmation email skipped:', configCheck?.error);
    return { success: false, error: configCheck?.error, skipped: true };
  }

  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`
      },
      body: JSON.stringify({
        type: 'order_confirmation',
        to: email,
        subject: `Siparişiniz Alındı - #${orderData?.orderId?.slice(0, 8)}`,
        data: {
          orderId: orderData?.orderId,
          orderTotal: orderData?.orderTotal,
          items: orderData?.items,
          shippingAddress: orderData?.shippingAddress,
          customerName: orderData?.customerName
        }
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result?.error || 'Failed to send order confirmation email');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Order confirmation email error:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send low stock alert email to admin
 * @param {string} email - Admin email address
 * @param {Object} productData - Product details (title, sku, currentStock, threshold)
 * @returns {Promise<Object>} Response with success status
 */
export const sendInventoryAlertEmail = async (email, productData) => {
  const configCheck = validateEmailConfig();
  if (!configCheck?.isValid) {
    console.warn('Inventory alert email skipped:', configCheck?.error);
    return { success: false, error: configCheck?.error, skipped: true };
  }

  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`
      },
      body: JSON.stringify({
        type: 'inventory_alert',
        to: email,
        subject: `⚠️ Düşük Stok Uyarısı - ${productData?.productTitle}`,
        data: {
          productTitle: productData?.productTitle,
          productSku: productData?.productSku,
          currentStock: productData?.currentStock,
          threshold: productData?.threshold,
          productId: productData?.productId
        }
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result?.error || 'Failed to send inventory alert email');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Inventory alert email error:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send marketplace inquiry notification to seller
 * @param {string} email - Seller's email address
 * @param {Object} inquiryData - Inquiry details (buyerName, itemTitle, message, itemId)
 * @returns {Promise<Object>} Response with success status
 */
export const sendMarketplaceInquiryEmail = async (email, inquiryData) => {
  const configCheck = validateEmailConfig();
  if (!configCheck?.isValid) {
    console.warn('Marketplace inquiry email skipped:', configCheck?.error);
    return { success: false, error: configCheck?.error, skipped: true };
  }

  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`
      },
      body: JSON.stringify({
        type: 'marketplace_inquiry',
        to: email,
        subject: `Yeni Mesaj: ${inquiryData?.itemTitle}`,
        data: {
          buyerName: inquiryData?.buyerName,
          itemTitle: inquiryData?.itemTitle,
          messageContent: inquiryData?.messageContent,
          itemId: inquiryData?.itemId
        }
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result?.error || 'Failed to send marketplace inquiry email');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Marketplace inquiry email error:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send order status update email
 * @param {string} email - Customer's email address
 * @param {Object} orderData - Order and tracking details
 * @returns {Promise<Object>} Response with success status
 */
export const sendOrderStatusUpdateEmail = async (email, orderData) => {
  const configCheck = validateEmailConfig();
  if (!configCheck?.isValid) {
    console.warn('Order status update email skipped:', configCheck?.error);
    return { success: false, error: configCheck?.error, skipped: true };
  }

  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`
      },
      body: JSON.stringify({
        type: 'order_status_update',
        to: email,
        subject: `Sipariş Durumu Güncellendi - #${orderData?.orderId?.slice(0, 8)}`,
        data: {
          orderId: orderData?.orderId,
          orderStatus: orderData?.orderStatus,
          trackingNumber: orderData?.trackingNumber,
          carrierName: orderData?.carrierName,
          estimatedDelivery: orderData?.estimatedDelivery,
          trackingUrl: orderData?.trackingUrl,
          customerName: orderData?.customerName,
          eventDescription: orderData?.eventDescription
        }
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result?.error || 'Failed to send order status update email');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Order status update email error:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Send delivery confirmation email with photos
 * @param {string} email - Customer's email address
 * @param {Object} deliveryData - Delivery confirmation details
 * @returns {Promise<Object>} Response with success status
 */
export const sendDeliveryConfirmationEmail = async (email, deliveryData) => {
  const configCheck = validateEmailConfig();
  if (!configCheck?.isValid) {
    console.warn('Delivery confirmation email skipped:', configCheck?.error);
    return { success: false, error: configCheck?.error, skipped: true };
  }

  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANON_KEY}`
      },
      body: JSON.stringify({
        type: 'delivery_confirmation',
        to: email,
        subject: `Siparişiniz Teslim Edildi - #${deliveryData?.orderId?.slice(0, 8)}`,
        data: {
          orderId: deliveryData?.orderId,
          trackingNumber: deliveryData?.trackingNumber,
          recipientName: deliveryData?.recipientName,
          deliveryTimestamp: deliveryData?.deliveryTimestamp,
          deliveryPhotoUrls: deliveryData?.deliveryPhotoUrls,
          carrierName: deliveryData?.carrierName,
          customerName: deliveryData?.customerName
        }
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result?.error || 'Failed to send delivery confirmation email');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Delivery confirmation email error:', error);
    return { success: false, error: error?.message };
  }
};